import type { PostEntry } from "./PostPlanBuilder";
import type { GuideStep } from "./guideApi";
import { useState, useEffect } from "react";

interface PostImplementationGuideProps {
  entry: PostEntry;

  matchedUsecases: string[];
  activeUsecase: string | null;
  guideSteps: GuideStep[];

  expanded: boolean;
  currentStepIndex: number;

  onToggle: () => void;
  onUsecaseChange: (usecase: string) => void;
  onStepChange: (step: number) => void;

  formatDate: (date: Date) => string;
  usecaseLabels: Record<string, string>;
  guideImageUrl: (path: string) => string;
}

export default function PostImplementationGuide({
  entry,
  matchedUsecases,
  activeUsecase,
  guideSteps,
  expanded,
  currentStepIndex,
  onToggle,
  onUsecaseChange,
  onStepChange,
  formatDate,
  usecaseLabels,
  guideImageUrl,
}: PostImplementationGuideProps) {
  return (
    <div className="post-plan-entry-umsetzung">
        <button
            type="button"
            className="post-plan-guide-toggle"
            onClick={onToggle}
        >
            <span className="post-plan-guide-toggle__label">
                Umsetzung
            </span>

            <span className="post-plan-guide-toggle__state">
                {expanded ? "Einklappen" : "Anzeigen"}
                <span aria-hidden="true">
                {expanded ? "▲" : "▼"}
                </span>
            </span>
        </button>

      {expanded && (
        <div className="post-plan-guide-content">
          {matchedUsecases.length > 1 && (
            <div className="post-plan-guide-tabs">
              {matchedUsecases.map((usecase) => (
                <button
                  key={usecase}
                  type="button"
                  className={`post-plan-guide-tab ${
                    activeUsecase === usecase ? "active" : ""
                  }`}
                  onClick={() => onUsecaseChange(usecase)}
                >
                  {usecaseLabels[usecase] ?? usecase}
                </button>
              ))}
            </div>
          )}

          {matchedUsecases.length === 0 ? (
            <p className="post-plan-guide-missing">
              Kein passender Use Case für{" "}
              {entry.postType ?? "diesen Post-Type"} aktiv.
            </p>
          ) : guideSteps.length === 0 ? (
            <p className="post-plan-guide-missing">
              Anleitung wird geladen...
            </p>
          ) : (
            <GuideStepper
              steps={guideSteps}
              currentStepIndex={currentStepIndex}
              onStepChange={onStepChange}
              guideImageUrl={guideImageUrl}
            />
          )}

          <p className="post-plan-guide-final-step">
            Am {formatDate(entry.scheduledDate)} veröffentlichen.
          </p>
        </div>
      )}
    </div>
  );
}

interface GuideStepperProps {
  steps: GuideStep[];
  currentStepIndex: number;
  onStepChange: (step: number) => void;
  guideImageUrl: (path: string) => string;
}

function GuideStepper({
  steps,
  currentStepIndex,
  onStepChange,
  guideImageUrl,
}: GuideStepperProps) {
  const safeIndex = Math.min(
    currentStepIndex,
    steps.length - 1
  );

  const currentStep = steps[safeIndex];
  const currentImagePath = currentStep.imagePath;
  const [enlargedImage, setEnlargedImage] = useState<string | null>(null);

  useEffect(() => {
  if (!enlargedImage) return;

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      setEnlargedImage(null);
    }
  };

  window.addEventListener("keydown", handleKeyDown);

  return () => {
    window.removeEventListener("keydown", handleKeyDown);
  };
}, [enlargedImage]);

  return (
    <div className="post-plan-guide-stepper">
      <div className="post-plan-guide-stepper-header">
        Schritt {safeIndex + 1} / {steps.length}
      </div>

      <div className="post-plan-guide-step">
        <div className="post-plan-guide-step-title">
          {currentStep.title}
        </div>

        <p>{currentStep.description}</p>

       {currentImagePath && (
            <img
              src={guideImageUrl(currentImagePath)}
              alt={currentStep.title}
              className="post-plan-guide-step-image"
              onClick={() =>
                setEnlargedImage(guideImageUrl(currentImagePath))
              }
              title="Zum Vergrößern klicken"
            />
          )}
      </div>

      {enlargedImage && (
          <div
            className="guide-lightbox"
            onClick={() => setEnlargedImage(null)}
          >
            <div
              className="guide-lightbox__content"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="guide-lightbox__close"
                onClick={() => setEnlargedImage(null)}
                aria-label="Vergrößerte Ansicht schließen"
              >
                ×
              </button>

              <img
                src={enlargedImage}
                alt="Vergrößerte Anleitung"
              />
            </div>
          </div>
        )}

      <div className="post-plan-guide-stepper-nav">
        <button
          type="button"
          onClick={() =>
            onStepChange(Math.max(0, safeIndex - 1))
          }
          disabled={safeIndex === 0}
        >
          ‹ Zurück
        </button>

        <button
          type="button"
          onClick={() =>
            onStepChange(
              Math.min(steps.length - 1, safeIndex + 1)
            )
          }
          disabled={safeIndex === steps.length - 1}
        >
          Weiter ›
        </button>
      </div>
    </div>
  );
}